import { CognitoUser } from "amazon-cognito-identity-js";
import React, { useLayoutEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import styled, { createGlobalStyle, keyframes } from "styled-components";
import { colors, defaultRadius, palette } from "../../../styles/constants";
import useMediaQuery from "../../../util/useMediaQuery";
import { MfaType, SelectMfaTypeOptions } from "../../types";
import BackgroundVeil from "./BackgroundVeil";
import DinteroLogo from "./dintero-dark.svg";
import ForgotPassword from "./ForgotPassword";
import Login from "./Login";
import SelectMfaType from "./SelectMfaType";
import VerifyForgotPassword from "./VerifyForgotPassword";
import VerifyMfa from "./VerifyMfa";

type TransitionDirection = "forward" | "back";

// Runs the state update inside a view transition so the card morphs between
// views. The direction is exposed on <html> so the CSS can pick slide side.
const withViewTransition = (
    direction: TransitionDirection,
    update: () => void,
) => {
    if (
        !document.startViewTransition ||
        window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
        update();
        return;
    }
    const root = document.documentElement;
    root.dataset.loginTransition = direction;
    const transition = document.startViewTransition(() => flushSync(update));
    transition.finished.finally(() => {
        delete root.dataset.loginTransition;
    });
};

const LoginPage = () => {
    const [loginView, setLoginView] = useState<
        | "login"
        | "forgotPassword"
        | "verifyForgotPassword"
        | "mfaRequired"
        | "selectMfaType"
    >("login");
    const [shake, setShake] = useState<boolean>(false);
    const [init, setInit] = useState<boolean>(true);

    const [username, setUsername] = useState<string>("");
    const [cognitoUser, setCognitoUser] = useState<CognitoUser | undefined>();
    const [mfaType, setMfaType] = useState<MfaType | undefined>();
    const [selectMfaTypeOptions, setSelectMfaTypeOptions] = useState<
        undefined | SelectMfaTypeOptions
    >();
    if (loginView === "login") {
        return (
            <LoginPageWrapper
                shake={shake}
                setShake={setShake}
                init={init}
                setInit={setInit}
            >
                <Login
                    username={username}
                    setForgotPassword={(username: string) => {
                        withViewTransition("forward", () => {
                            setUsername(username);
                            setLoginView("forgotPassword");
                            setCognitoUser(undefined);
                        });
                    }}
                    setMfaRequired={(cognitoUser: CognitoUser, mfaType) => {
                        withViewTransition("forward", () => {
                            setCognitoUser(cognitoUser);
                            setLoginView("mfaRequired");
                            setMfaType(mfaType);
                        });
                    }}
                    setSelectMfaRequired={(
                        cognitoUser: CognitoUser,
                        options,
                    ) => {
                        withViewTransition("forward", () => {
                            setCognitoUser(cognitoUser);
                            setSelectMfaTypeOptions(options);
                            setLoginView("selectMfaType");
                        });
                    }}
                    setShake={setShake}
                />
            </LoginPageWrapper>
        );
    }
    if (loginView === "forgotPassword") {
        return (
            <LoginPageWrapper
                shake={shake}
                setShake={setShake}
                init={init}
                setInit={setInit}
            >
                <ForgotPassword
                    username={username}
                    setSubmitUsername={(username: string) => {
                        withViewTransition("forward", () => {
                            setUsername(username);
                            setLoginView("verifyForgotPassword");
                            setCognitoUser(undefined);
                        });
                    }}
                    setBack={(username: string) => {
                        withViewTransition("back", () => {
                            setUsername(username);
                            setLoginView("login");
                            setCognitoUser(undefined);
                        });
                    }}
                    setShake={setShake}
                />
            </LoginPageWrapper>
        );
    }
    if (loginView === "mfaRequired" && cognitoUser) {
        return (
            <LoginPageWrapper
                shake={shake}
                setShake={setShake}
                init={init}
                setInit={setInit}
            >
                <VerifyMfa
                    cognitoUser={cognitoUser}
                    setBack={() => {
                        withViewTransition("back", () => {
                            setLoginView("login");
                            setCognitoUser(undefined);
                        });
                    }}
                    setShake={setShake}
                    mfaType={mfaType || "SMS_MFA"}
                />
            </LoginPageWrapper>
        );
    }
    if (loginView === "selectMfaType" && cognitoUser && selectMfaTypeOptions) {
        return (
            <LoginPageWrapper
                shake={shake}
                setShake={setShake}
                init={init}
                setInit={setInit}
            >
                <SelectMfaType
                    cognitoUser={cognitoUser}
                    setBack={() => {
                        withViewTransition("back", () => {
                            setLoginView("login");
                            setCognitoUser(undefined);
                        });
                    }}
                    setMfaRequired={(cognitoUser: CognitoUser, mfaType) => {
                        withViewTransition("forward", () => {
                            setCognitoUser(cognitoUser);
                            setLoginView("mfaRequired");
                            setMfaType(mfaType);
                        });
                    }}
                    setShake={setShake}
                    selectMfaTypeOptions={selectMfaTypeOptions}
                />
            </LoginPageWrapper>
        );
    }
    return (
        <LoginPageWrapper
            shake={shake}
            setShake={setShake}
            init={init}
            setInit={setInit}
        >
            <VerifyForgotPassword
                username={username}
                setBack={() => {
                    withViewTransition("back", () => {
                        setLoginView("login");
                        setCognitoUser(undefined);
                    });
                }}
                setMfaRequired={(cognitoUser: CognitoUser, mfaType) => {
                    withViewTransition("forward", () => {
                        setCognitoUser(cognitoUser);
                        setLoginView("mfaRequired");
                        setMfaType(mfaType);
                    });
                }}
                setShake={setShake}
                setSelectMfaRequired={(cognitoUser: CognitoUser, options) => {
                    withViewTransition("forward", () => {
                        setCognitoUser(cognitoUser);
                        setSelectMfaTypeOptions(options);
                        setLoginView("selectMfaType");
                    });
                }}
            />
        </LoginPageWrapper>
    );
};

const LoginPageWrapper = ({
    children,
    shake,
    setShake,
    init,
    setInit,
}: {
    children: React.ReactNode;
    shake: boolean;
    setShake: (value: boolean) => void;
    init: boolean;
    setInit: (value: boolean) => void;
}) => {
    // Not mounted on mobile, so the animation loop doesn't run there
    const isMobile = useMediaQuery("(max-width: 768px)");
    const contentRef = useRef<HTMLDivElement>(null);
    // Number the reveal elements in document order so they stagger in.
    // Views mounted by a view transition skip the reveal, the slide is enough
    // and animating inside the live snapshot is janky in Firefox and Safari.
    useLayoutEffect(() => {
        const transitioning =
            !!document.documentElement.dataset.loginTransition;
        contentRef.current
            ?.querySelectorAll<HTMLElement>("[data-reveal]")
            .forEach((element, index) => {
                element.style.setProperty("--reveal-index", `${index}`);
                if (transitioning) {
                    element.style.animation = "none";
                }
            });
    });
    return (
        <>
            <ViewTransitionStyles />
            {!isMobile && (
                <BackgroundWrapper>
                    <BackgroundVeil />
                </BackgroundWrapper>
            )}
            <LoginContainer
                onAnimationEnd={(event) => {
                    // Ignore reveal animations bubbling up from the content
                    if (event.target !== event.currentTarget) {
                        return;
                    }
                    setInit(false);
                    setShake(false);
                }}
                className={[shake ? "shake" : "", init ? "init" : ""].join(" ")}
            >
                <LogoWrapper>
                    <img src={DinteroLogo} alt="Dintero Logo" />
                </LogoWrapper>
                <ContentWrapper ref={contentRef}>{children}</ContentWrapper>
            </LoginContainer>
        </>
    );
};

const shake = keyframes`
  10%, 90% {
    transform: translateX(-2px);
  }
  20%, 80% {
    transform: translateX(4px);
  }

  30%, 50%, 70% {
    transform: translateX(-6px);
  }

  40%, 60% {
    transform: translateX(8px);
}`;

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10%);
    }
  to {
    opacity: 1;
    transform: translateY(0);
}`;

const slideOut = keyframes`
  to {
    opacity: 0;
    transform: translateX(calc(-1 * var(--slide-offset, 24px)));
  }`;

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(var(--slide-offset, 24px));
  }`;

// Only a "from" keyframe so elements end on their own styles
const reveal = keyframes`
  from {
    opacity: 0;
    transform: translateY(12px);
  }`;

const LoginContainer = styled.div`
    z-index: 1;
    width: 562px;
    padding: 60px 37px;
    margin: auto;
    box-shadow: 137px 194px 95px 0px rgba(0, 0, 0, 0.01), 77px 109px 80px 0px rgba(0, 0, 0, 0.02), 34px 49px 60px 0px rgba(0, 0, 0, 0.03), 9px 12px 33px 0px rgba(0, 0, 0, 0.04);
    background-color: white;
    transform-origin: center;
    view-transition-name: login-card;
    border: 1px solid ${colors.borderDark};
    border-radius: ${defaultRadius};

    &.init {
        animation: ${fadeIn} 0.5s cubic-bezier(0.36, 0.07, 0.19, 0.97);
    }

    &.shake {
        animation: ${shake} 0.5s cubic-bezier(0.36, 0.07, 0.19, 0.97) both;
    }

    @media (max-width: 768px) {
        width: 100%;
        height: 100vh;
        margin: 0;
        bottom: 0;
    }

    & > * {
        max-width: 328px;
        margin: 0 auto;
    }

    [data-reveal] {
        animation: ${reveal} 0.5s cubic-bezier(0.22, 1, 0.36, 1) backwards;
        animation-delay: calc(80ms + var(--reveal-index, 0) * 45ms);
    }

    @media (prefers-reduced-motion: reduce) {
        [data-reveal] {
            animation: none;
        }
    }
`;

// Named separately from the card so the content animates while the card morphs
const ContentWrapper = styled.div`
    view-transition-name: login-content;

    & > * {
        max-width: 328px;
        margin: 0 auto;
    }

    [data-reveal] {
        animation: ${reveal} 0.5s cubic-bezier(0.22, 1, 0.36, 1) backwards;
        animation-delay: calc(80ms + var(--reveal-index, 0) * 45ms);
    }

    @media (prefers-reduced-motion: reduce) {
        [data-reveal] {
            animation: none;
        }
    }
`;

const ViewTransitionStyles = createGlobalStyle`
    /*
     * Leave the page out of the capture so the WebGL veil keeps rendering
     * directly instead of being re-snapshotted every frame. Only the named
     * card, logo and content are captured.
     */
    :root {
        view-transition-name: none;
    }

    ::view-transition-group(login-card),
    ::view-transition-group(login-logo),
    ::view-transition-group(login-content) {
        animation-duration: 0.35s;
        animation-timing-function: cubic-bezier(0.36, 0.07, 0.19, 0.97);
    }

    ::view-transition-old(login-content) {
        animation: ${slideOut} 0.2s cubic-bezier(0.36, 0.07, 0.19, 0.97) both;
    }

    ::view-transition-new(login-content) {
        animation: ${slideIn} 0.35s cubic-bezier(0.36, 0.07, 0.19, 0.97) 0.1s both;
    }

    :root[data-login-transition="back"]::view-transition-old(login-content),
    :root[data-login-transition="back"]::view-transition-new(login-content) {
        --slide-offset: -24px;
    }
`;

const BackgroundWrapper = styled.div`
    z-index: 0;
    position: fixed;
    inset: 0;
    background: ${palette.neutral[900]};
    overflow: hidden;
    canvas {
        display: block;
        width: 100%;
        height: 100%;
    }
`;

const LogoWrapper = styled.div`
    view-transition-name: login-logo;
    margin-bottom: 37px;
    width: 135px;
    position: relative;
    margin-inline: auto;
    img {
        width: 100%;
        height: auto;
    }
`;

export default LoginPage;
